'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useEffect, useRef, useState } from 'react'

import { useFamilies } from '../../../contexts/FamiliesProvider/FamiliesProvider'
import { useRegions } from '../../../contexts/RegionsProvider/RegionsProvider'
import { useUnit } from '../../../contexts/UnitsProvider/UnitsProvider'
import { getClientSizeGuides } from '../../../services/getSizeGuides/client/getClientSizeGuides'
import type { SizeGuideList } from '../../../types'
import { SizeGuidesList } from '../SizeGuidesList/SizeGuidesList'

interface InitialSizeGuidesListProps {
	readonly initialSizeGuidesList: SizeGuideList[]
	readonly className?: string
}

const CELL_HEIGHT = 46

export function SizeGuidesListContainer({
	initialSizeGuidesList,
	className,
}: InitialSizeGuidesListProps): React.ReactNode {
	const {
		country: { languageISO },
	} = useMasterData()
	const { selectedFamilyId } = useFamilies()
	const { selectedRegionId } = useRegions()
	const { selectedUnit } = useUnit()

	const [sizeGuideList, setSizeGuidesList] = useState(initialSizeGuidesList)
	const firstRenderRef = useRef(true)

	useEffect(() => {
		const fetchSizeGuides = async () => {
			const { sizeGuides: fetchedSizeGuides } = await getClientSizeGuides({
				languageISO,
				familyId: selectedFamilyId,
				regionId: selectedRegionId,
			})
			setSizeGuidesList(fetchedSizeGuides)
		}

		if (!firstRenderRef.current) {
			fetchSizeGuides()
		} else {
			firstRenderRef.current = false
		}
	}, [selectedFamilyId, selectedRegionId])

	if (!sizeGuideList.length) {
		return null
	}

	return (
		<SizeGuidesList
			cellHeight={CELL_HEIGHT}
			data={sizeGuideList}
			unit={selectedUnit}
			isHighlightable
			isTransformable
			className={className}
		/>
	)
}
